import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Apollo, QueryRef } from 'apollo-angular';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DialogEmailComponent } from 'src/app/features/dialog/dialog-email/dialog-email.component';
import { ManifestService } from '../../documents/manifest.service';
// import { DialogEmailComponent } from "src/app/features/dialog/dialog-email/dialog-email.component";
import {
	AddEmailOutDocument,
	AddEmailTemplateLocationContactDocument,
	AvailabilityType,
	CopyEmailTemplateDocument,
	DeleteEmailTemplateDocument,
	EmailAddressInput,
	EmailInput,
	EmailTemplateType,
	Flight,
	GetAllEmailTemplatesDocument,
	GetEmailTemplateByIdDocument,
	GetEmailTemplateByLanguageDocument,
	GetEmailTemplateDocument,
	GetEmailTemplatesDocument,
	TenantCurrency,
} from '../../graphql/generated/gen-types';
import { SnackBar } from '../../utility/snackBar';
import { UserService } from '../admin/user.service';
import { FlightService } from '../flight/flight.service';
import { TenantSettingService } from '../tenantSetting/tenant-setting.service';
import { TranslateCustomService } from '../../utility/translate-custom.service';
import { FileService } from 'src/app/core/services/file/file.service';
import { DialogEmailMultipleComponent } from 'src/app/features/dialog/dialog-email-multiple/dialog-email-multiple.component';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(timezone);
dayjs.extend(utc);

@Injectable({
	providedIn: 'root',
})
export class EmailService {
	userProfile;
	tenantEmailFrom;
	tenantEmailReplyTo;
	tenantEmailAccountant: EmailAddressInput;
	timezone: string;
	currency: TenantCurrency;

	constructor(
		private apollo: Apollo,
		private dialog: MatDialog,
		private translateCustom: TranslateCustomService,
		private tenantSettingService: TenantSettingService,
		private userService: UserService,
		private snackBar: SnackBar,
		private manifestService: ManifestService,
		private flightService: FlightService,
		private fileService: FileService,
	) {
		this.tenantSettingService.getTenantSetting().valueChanges.subscribe((tenantSettingResult) => {
			this.tenantEmailFrom = tenantSettingResult.data.tenantSetting.mailFrom;
			this.tenantEmailReplyTo = tenantSettingResult.data.tenantSetting.mailReplyTo;
			if (tenantSettingResult.data.tenantSetting.mailAccountant) {
				this.tenantEmailAccountant = {
					email: tenantSettingResult.data.tenantSetting?.mailAccountant?.email,
					name: tenantSettingResult.data.tenantSetting?.mailAccountant?.name,
				};
			}
			this.currency = tenantSettingResult.data.tenantSetting.operatorSettings?.currency;
			this.timezone = tenantSettingResult.data.tenantSetting.operatorSettings?.timezone || 'Europe/Brussels';
		});

		this.userService.userProfile.subscribe((userProfile) => {
			this.userProfile = userProfile;
		});
	}

	public getAllEmailTemplates(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetAllEmailTemplatesDocument,
		});
	}

	// Google Places formattedAddress ends with the country (e.g. "Street 1, 1234 AB City, Netherlands");
	// operators don't want the country shown for the meeting point, so drop that trailing segment.
	private stripTrailingCountry(address: string | null | undefined): string {
		if (!address) return '';
		const parts = address
			.split(',')
			.map((p) => p.trim())
			.filter(Boolean);
		if (parts.length <= 2) return address;
		return parts.slice(0, -1).join(', ');
	}

	private getEmailTemplate(type: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetEmailTemplateDocument,
			variables: {
				type,
			},
		});
	}
	private getEmailTemplateByID(id: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetEmailTemplateByIdDocument,
			variables: {
				id,
			},
		});
	}
	public copyEmailTemplate(id: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: CopyEmailTemplateDocument,
				variables: {
					emailTemplateId: id,
				},
				refetchQueries: [GetAllEmailTemplatesDocument],
			})
			.pipe(map((result: any) => result.data));
	}
	public addEmailTemplateLocationContact(): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: AddEmailTemplateLocationContactDocument,
				refetchQueries: [GetAllEmailTemplatesDocument],
			})
			.pipe(map((result: any) => result.data));
	}

	public deleteEmailTemplate(id: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: DeleteEmailTemplateDocument,
				variables: {
					emailTemplateId: id,
				},
				refetchQueries: [GetAllEmailTemplatesDocument],
			})
			.pipe(map((result: any) => result.data));
	}

	private getEmailTemplates(type: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetEmailTemplatesDocument,
			variables: {
				type,
			},
		});
	}

	private getEmailTemplateByLanguage(type: string, lang: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetEmailTemplateByLanguageDocument,
			variables: {
				type,
				lang,
			},
		});
	}

	public addEmailOut(emailInput: EmailInput): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: AddEmailOutDocument,
				variables: {
					emailInput,
				},
			})
			.pipe(map((result: any) => result.data));
	}

	private sendMail(emails: any[]) {
		if (emails.length === 0) return;
		const dialogRef = this.dialog.open(DialogEmailComponent, {
			width: window.innerWidth <= 360 ? '95%' : '80%',
			maxWidth: 'none',

			data: {
				emails: emails,
				readOnly: false,
			},
		});
	}

	private sendMailMultiple(emails: any[], templates: string[]) {
		if (emails.length === 0) return;
		const dialogRef = this.dialog.open(DialogEmailMultipleComponent, {
			width: window.innerWidth <= 360 ? '95%' : '80%',
			maxWidth: 'none',
			data: {
				emails,
				templates,
				readOnly: false,
			},
		});
	}

	public async sendBookingConfirmationMail(booking?: any, flight?: any, lang?) {
		// Issue #1: Validate booking and email address exists
		if (!booking) {
			this.snackBar.openSnackBarError('Booking is missing');
			return;
		}

		if (!booking?.contactEmail) {
			this.snackBar.openSnackBarError('Booking contact email is missing');
			return;
		}

		// Issue #3: Validate tenant settings are loaded
		if (!this.tenantEmailFrom || !this.tenantEmailReplyTo) {
			this.snackBar.openSnackBarError('Email configuration is not loaded. Please try again.');
			return;
		}

		let language = booking.contactLanguage;
		if (lang) language = lang;

		try {
			const result = await firstValueFrom(this.getEmailTemplate(EmailTemplateType.BookingConfirmEmail).valueChanges);
			try {
				// Issue #2: Validate language key exists in template
				const templateData = result.data.getEmailTemplate.template[language];
				if (!templateData) {
					const availableLanguages = Object.keys(result.data.getEmailTemplate.template).join(', ');
					this.snackBar.openSnackBarError(`Email template language '${language}' not found. Available: ${availableLanguages}`);
					return;
				}

				const emailTemplate = templateData;
				// parse the template with the context
				const emailSubject = this.parseEmailTemplate(emailTemplate.subject, language, booking, flight);
				const emailBody = this.parseEmailTemplate(emailTemplate.body, language, booking, flight);
				const emailFrom = this.tenantEmailFrom;
				const emailReplyTo = this.tenantEmailReplyTo;
				const attachments = emailTemplate.attachments;

				const emailInput = {
					bookingId: booking.id,
					crewId: null,
					from: {
						email: emailFrom.email,
						name: emailFrom.name,
					},
					replyTo: {
						email: emailReplyTo.email,
						name: emailReplyTo.name,
					},
					to: booking.contactEmail,
					subject: emailSubject,
					text: emailBody,
					html: emailBody,
					attachments,
				};

				// get all destination addresses
				const emails = [emailInput];

				// send the emails
				this.sendMail(emails);
			} catch (err) {
				// Catch any errors that occur during template processing
				this.snackBar.openSnackBarError(err.message);
			}
		} catch (err) {
			// Issue #3: Provide clear error message for template retrieval failure
			this.snackBar.openSnackBarError(err.message);
		}
	}

	public async sendBookingConfirmationMailMultiple(booking?: any, flight?: any) {
		if (!booking) {
			this.snackBar.openSnackBarError('Booking is missing');
			return;
		}

		if (!booking?.contactEmail) {
			this.snackBar.openSnackBarError('Booking contact email is missing');
			return;
		}

		if (!this.tenantEmailFrom || !this.tenantEmailReplyTo) {
			this.snackBar.openSnackBarError('Email configuration is not loaded. Please try again.');
			return;
		}

		try {
			const result = await firstValueFrom(this.getEmailTemplates(EmailTemplateType.BookingConfirmEmail).valueChanges);
			const templates = result.data.getEmailTemplates.map((t) => t.title);
			const emails = [];

			result.data.getEmailTemplates.forEach((emailTemplate) => {
				const language = booking.contactLanguage;
				const template = emailTemplate.template[language] ? emailTemplate.template[language] : emailTemplate.template['en'];
				const emailSubject = this.parseEmailTemplate(template.subject, language, booking, flight);
				const emailBody = this.parseEmailTemplate(template.body, language, booking, flight);
				const emailAttachments = template.attachments;
				const emailFrom = this.tenantEmailFrom;
				const emailReplyTo = this.tenantEmailReplyTo;

				const emailInput = {
					title: emailTemplate.title,
					bookingId: booking.id,
					crewId: null,
					from: {
						email: emailFrom.email,
						name: emailFrom.name,
					},
					replyTo: {
						email: emailReplyTo.email,
						name: emailReplyTo.name,
					},
					to: booking.contactEmail,
					contactName: booking.contactName,
					language,
					subject: emailSubject,
					text: emailBody,
					html: emailBody,
					attachments: emailAttachments,
				};

				emails.push(emailInput);
			});

			this.sendMailMultiple(emails, templates);
		} catch (err) {
			this.snackBar.openSnackBarError(err.message);
		}
	}

	public async sendCrewLoginMail(crew: any) {
		const language = crew.language;
		try {
			const result = await firstValueFrom(this.getEmailTemplateByLanguage(EmailTemplateType.CrewOtpEmail, language).valueChanges);
			const emailTemplate = result.data.getEmailTemplateByLanguage;
			// copy the template (no parsing needed)
			const emailSubject = emailTemplate.subject;
			let emailBody = emailTemplate.body;
			const emailFrom = this.tenantEmailFrom;
			const emailReplyTo = this.tenantEmailReplyTo;

			const emailInput = {
				bookingId: null,
				crewId: crew.id,
				from: {
					email: emailFrom.email,
					name: emailFrom.name,
				},
				replyTo: {
					email: emailReplyTo.email,
					name: emailReplyTo.name,
				},
				to: crew.emailNotifications,
				subject: emailSubject,
				text: emailBody,
				html: emailBody,
				attachments: [],
			};

			// get all destination addresses
			const emails = [emailInput];

			// send the emails
			this.sendMail(emails);
		} catch (err) {
			this.snackBar.openSnackBarError(err.message);
		}
	}

	public async sendFlightBookingsMail(language, flight?: any) {
		try {
			const result = await firstValueFrom(this.getEmailTemplate(EmailTemplateType.BookingFlightConfirmEmail).valueChanges);
			const emailTemplate = result.data.getEmailTemplate.template[language];
			const emails = [];
			// parse the template with the context
			flight?.bookings.list
				.filter((b) => b.contactLanguage === language)
				.forEach((booking) => {
					const emailSubject = this.parseEmailTemplate(emailTemplate.subject, language, booking, flight);
					const emailBody = this.parseEmailTemplate(emailTemplate.body, language, booking, flight);
					const emailFrom = this.tenantEmailFrom;
					const emailReplyTo = this.tenantEmailReplyTo;

					const emailInput = {
						bookingId: booking.id,
						crewId: null,
						from: {
							email: emailFrom.email,
							name: emailFrom.name,
						},
						replyTo: {
							email: emailReplyTo.email,
							name: emailReplyTo.name,
						},
						to: booking.contactEmail,
						subject: emailSubject,
						text: emailBody,
						html: emailBody,
						attachments: [],
					};

					emails.push(emailInput);
				});

			// send the emails
			this.sendMail(emails);
		} catch (err) {
			this.snackBar.openSnackBarError(err.message);
		}
	}

	public async sendFlightBookingsMailMultiple(flight?: any) {
		// get the email templates of type BookingFlightConfirmEmail
		try {
			const result = await firstValueFrom(this.getEmailTemplates(EmailTemplateType.BookingFlightConfirmEmail).valueChanges);
			const templates = result.data.getEmailTemplates.map((t) => t.title);
			const emails = [];
			// for each title
			result.data.getEmailTemplates.forEach((emailTemplate) => {
				// parse the template with the context
				flight?.bookings.list.forEach((booking) => {
					const language = booking.contactLanguage;
					// default en template
					const template = emailTemplate.template[language] ? emailTemplate.template[language] : emailTemplate.template['en'];
					const emailSubject = this.parseEmailTemplate(template.subject, language, booking, flight);
					const emailBody = this.parseEmailTemplate(template.body, language, booking, flight);
					const emailAttachments = template.attachments;
					const emailFrom = this.tenantEmailFrom;
					const emailReplyTo = this.tenantEmailReplyTo;

					const emailInput = {
						title: emailTemplate.title,
						bookingId: booking.id,
						crewId: null,
						from: {
							email: emailFrom.email,
							name: emailFrom.name,
						},
						replyTo: {
							email: emailReplyTo.email,
							name: emailReplyTo.name,
						},
						to: booking.contactEmail,
						contactName: booking.contactName,
						language,
						subject: emailSubject,
						text: emailBody,
						html: emailBody,
						attachments: emailAttachments,
					};

					emails.push(emailInput);
				});
			});

			// send the emails
			this.sendMailMultiple(emails, templates);
		} catch (err) {
			this.snackBar.openSnackBarError(err.message);
		}
	}

	public async sendFlightLocationMail(flight: any) {
		const language = flight.location.contact.language ? flight.location.contact.language : 'nl';
		try {
			const result = await firstValueFrom(this.getEmailTemplateByID(flight.location.contact.emailTemplate).valueChanges);
			const emailTemplate = result.data.getEmailTemplateByID.template[language];
			const emails = [];
			const emailFrom = this.tenantEmailFrom;
			const emailReplyTo = this.tenantEmailReplyTo;
			const emailSubject = this.parseEmailTemplate(emailTemplate.subject, language, null, flight);
			const emailBody = this.parseEmailTemplate(emailTemplate.body, language, null, flight);
			const emailAttachments = emailTemplate.attachments;
			// parse the template with the context

			const emailInput = {
				flightId: flight.id,
				locationId: flight.location.id,
				bookingId: null,
				crewId: null,
				from: {
					email: emailFrom.email,
					name: emailFrom.name,
				},
				replyTo: {
					email: emailReplyTo.email,
					name: emailReplyTo.name,
				},
				to: flight.location.contact.email,
				subject: emailSubject,
				text: emailBody,
				html: emailBody,
				attachments: emailAttachments,
			};

			emails.push(emailInput);

			// send the emails
			this.sendMail(emails);
		} catch (err) {
			this.snackBar.openSnackBarError(err.message);
		}
	}

	public async sendFlightEndMail(language, flight?: any) {
		try {
			const result = await firstValueFrom(this.getEmailTemplate(EmailTemplateType.BookingFlightCancelEmail).valueChanges);
			const emailTemplate = result.data.getEmailTemplate.template[language];
			const emails = [];
			// parse the template with the context
			flight?.bookings.list
				.filter((b) => b.contactLanguage === language)
				.forEach((booking) => {
					const emailSubject = this.parseEmailTemplate(emailTemplate.subject, language, booking, flight);
					const emailBody = this.parseEmailTemplate(emailTemplate.body, language, booking, flight);
					const emailFrom = this.tenantEmailFrom;
					const emailReplyTo = this.tenantEmailReplyTo;

					const emailInput = {
						bookingId: booking.id,
						crewId: null,
						from: {
							email: emailFrom.email,
							name: emailFrom.name,
						},
						replyTo: {
							email: emailReplyTo.email,
							name: emailReplyTo.name,
						},
						to: booking.contactEmail,
						subject: emailSubject,
						text: emailBody,
						html: emailBody,
						attachments: [],
					};

					emails.push(emailInput);
				});

			// send the emails
			this.sendMail(emails);
		} catch (err) {
			this.snackBar.openSnackBarError(err.message);
		}
	}

	public async sendFlightPilotMail(flight?: any) {
		if (!flight?.pilot) return;
		const language = flight.pilot.crew.language;

		try {
			const result = await firstValueFrom(this.getEmailTemplate(EmailTemplateType.PilotFlightEmail).valueChanges);
			const emailTemplate = result.data.getEmailTemplate.template[language];
			const emails = [];
			// parse the template with the context
			const emailSubject = this.parseEmailTemplate(emailTemplate.subject, language, null, flight);
			const emailBody = this.parseEmailTemplate(emailTemplate.body, language, null, flight);
			const emailFrom = this.tenantEmailFrom;
			const emailReplyTo = this.tenantEmailReplyTo;

			const emailInput = {
				bookingId: null,
				crewId: flight.pilot.crew.id,
				from: {
					email: emailFrom.email,
					name: emailFrom.name,
				},
				replyTo: {
					email: emailReplyTo.email,
					name: emailReplyTo.name,
				},
				to: flight.pilot.crew.emailNotifications ?? flight.pilot.crew.email,
				subject: emailSubject,
				text: emailBody,
				html: emailBody,
				attachments: [],
			};

			emails.push(emailInput);

			// send the emails
			this.sendMail(emails);
		} catch (err) {
			this.snackBar.openSnackBarError(err.message);
		}
	}

	public async sendFlightCrewMail(language, flight?: any) {
		try {
			const result = await firstValueFrom(this.getEmailTemplate(EmailTemplateType.CrewFlightEmail).valueChanges);
			const emailTemplate = result.data.getEmailTemplate.template[language];
			const emails = [];
			// parse the template with the context
			const emailSubject = this.parseEmailTemplate(emailTemplate.subject, language, null, flight);
			const emailBody = this.parseEmailTemplate(emailTemplate.body, language, null, flight);
			const emailFrom = this.tenantEmailFrom;
			const emailReplyTo = this.tenantEmailReplyTo;

			flight?.groundCrews
				.filter((c) => c.crew.language === language)
				.forEach((gc) => {
					const emailInput = {
						bookingId: null,
						crewId: gc.crew.id,
						from: {
							email: emailFrom.email,
							name: emailFrom.name,
						},
						replyTo: {
							email: emailReplyTo.email,
							name: emailReplyTo.name,
						},
						to: gc.crew.emailNotifications,
						subject: emailSubject,
						text: emailBody,
						html: emailBody,
						attachments: [],
					};

					emails.push(emailInput);
				});

			// send the emails
			this.sendMail(emails);
		} catch (err) {
			this.snackBar.openSnackBarError(err.message);
		}
	}

	public sendFlightPaymentDetails(flightId: string) {
		const emails = [];

		let crewId = null;
		if (this.userProfile.crew?.id) crewId = this.userProfile.crew.id;

		// send to operator or to crewNotification address
		let to = this.tenantEmailFrom.email;
		if (this.userProfile.crew?.emailNotifications) to = this.userProfile.crew?.emailNotifications;

		// generate document
		let attachments = [];
		this.flightService.get(flightId).valueChanges.subscribe(
			async (result) => {
				const paymentPDF = await this.manifestService.generatePaymentPDF('all', result.data.flight, false, false, this.currency);
				const file = new File([paymentPDF.pdf as BlobPart], paymentPDF.title);

				const uploadFile = this.fileService.uploadFile(file);
				uploadFile.uploadTask.then((res) => {
					attachments.push({ filename: paymentPDF.title, reference: res.metadata.fullPath });
					// parse the template with the context
					const emailSubject = paymentPDF.title;
					const emailBody = 'This is an auto-generated e-mail, please do not reply';
					const emailFrom = this.tenantEmailFrom;
					const emailReplyTo = this.tenantEmailReplyTo;

					const emailInput = {
						bookingId: null,
						crewId,
						from: {
							email: emailFrom.email,
							name: emailFrom.name,
						},
						replyTo: {
							email: emailReplyTo.email,
							name: emailReplyTo.name,
						},
						to,
						cc: this.tenantEmailAccountant ? [this.tenantEmailAccountant] : [],
						subject: emailSubject,
						text: emailBody,
						html: emailBody,
						attachments,
					};
					this.addEmailOut(emailInput).subscribe();
				});
			},
			(err) => {
				this.snackBar.openSnackBarError(err.message);
			},
		);
	}

	public parseEmailTemplate(emailTemplate: string, language: string, booking?: any, flight?: Flight) {
		const reHour = /{{ flight_hour }}/gi;
		const reHourMeeting = /{{ flight_meeting_hour }}/gi;
		const rePeriod = /{{ flight_period }}/gi;
		const reDate = /{{ flight_date }}/gi;
		const reBalloon = /{{ flight_balloon }}/gi;
		const reCity = /{{ location_city }}/gi;
		const reLocationName = /{{ location_name }}/gi;
		const reMeetingPoint = /{{ meeting_point }}/gi;
		const reAddress = /{{ location_address }}/gi;
		const reLocation = /{{ location_description }}/gi;
		const reBookingCode = /{{ booking_code }}/gi;
		const reBookingName = /{{ booking_name }}/gi;
		const reBookingComment = /{{ booking_comment }}/gi;
		const reBookingPassengers = /{{ booking_passengers }}/gi;
		const reBookingAdults = /{{ booking_adults }}/gi;
		const reBookingChilds = /{{ booking_childs }}/gi;
		const reBookingSpecial = /{{ booking_special }}/gi;
		const reBookingWeight = /{{ booking_weight }}/gi;
		const reBookingRegion = /{{ booking_region }}/gi;
		// const reBookingType = /{{ booking_type }}/gi;
		const reBookingAvailabilityDate = /{{ booking_availability_date }}/gi;
		const reBookingTotalAmount = /{{ booking_total_amount }}/gi;
		const reBookingOpenAmount = /{{ booking_open_amount }}/gi;
		const reBookingUpURL = /{{ booking_up_url }}/gi;
		const rePilotName = /{{ pilot_name }}/gi;
		const reCrewNames = /{{ crew_names }}/gi;

		// replace the variables with example text
		let emailText = emailTemplate ? emailTemplate : '';

		if (!flight) {
			const unknown = this.translateCustom.translate('FLIGHTS.unknown', language);
			emailText = emailText.replace(reHour, unknown);
			emailText = emailText.replace(reHourMeeting, unknown);
			emailText = emailText.replace(reDate, unknown);
			emailText = emailText.replace(reBalloon, unknown);
			emailText = emailText.replace(rePeriod, unknown);
			emailText = emailText.replace(reCity, unknown);
			emailText = emailText.replace(reLocationName, unknown);
			emailText = emailText.replace(reMeetingPoint, unknown);
			emailText = emailText.replace(reAddress, unknown);
			emailText = emailText.replace(reLocation, unknown);
			emailText = emailText.replace(rePilotName, unknown);
			emailText = emailText.replace(reCrewNames, unknown);
		} else {
			// Assuming `language` is a variable that holds the locale string (e.g., 'en')
			dayjs.locale(language);
			const localMoment = dayjs(flight?.hour).tz(this.timezone);
			const localMomentMeeting = dayjs(flight?.hourMeeting).tz(this.timezone);
			emailText = emailText.replace(reHour, localMoment.format('HH:mm'));
			emailText = emailText.replace(reHourMeeting, localMomentMeeting.format('HH:mm'));
			emailText = emailText.replace(reDate, localMoment.format('dddd DD MMMM YYYY'));
			emailText = emailText.replace(reBalloon, flight?.balloon?.name);
			emailText = emailText.replace(rePeriod, this.translateCustom.translate('FLIGHTS.' + flight?.period, language));
			emailText = emailText.replace(reCity, flight?.location?.city ? flight?.location?.city : '');
			emailText = emailText.replace(reLocationName, flight?.location?.name);
			emailText = emailText.replace(
				reMeetingPoint,
				this.stripTrailingCountry(flight?.meetingPointAddress || flight?.location?.meetingPointAddress),
			);
			emailText = emailText.replace(reAddress, flight?.location?.address ? flight?.location?.address : '');
			emailText = emailText.replace(reLocation, flight?.location?.description ? flight?.location?.description : '');
			emailText = emailText.replace(rePilotName, flight?.pilot?.crew?.name ? flight?.pilot?.crew?.name : '');
			emailText = emailText.replace(reCrewNames, flight?.groundCrews?.map((c) => c.crew.name).join(', '));
		}

		if (booking) {
			let localBookingAvailabilityDate = this.translateCustom.translate('BOOKINGS.' + booking.availabilityType, language);
			if (booking && booking.availabilityType === AvailabilityType.Date && booking.preferredFlights?.length > 0) {
				localBookingAvailabilityDate += dayjs(booking.preferredFlights[0].date).locale(language).format('dddd DD MMMM YYYY');
			}
			emailText = emailText.replace(reBookingCode, booking.bookingCode);
			emailText = emailText.replace(reBookingName, booking.contactName);
			emailText = emailText.replace(reBookingComment, booking.comments ? booking.comments : '');
			emailText = emailText.replace(
				reBookingAdults,
				(booking.passengerCounters.passengerCount - booking.passengerCounters.childCount).toString(),
			);
			emailText = emailText.replace(reBookingPassengers, booking.passengerCounters?.passengerCount.toString());
			emailText = emailText.replace(reBookingChilds, booking.passengerCounters?.childCount.toString());
			emailText = emailText.replace(reBookingSpecial, booking.passengerCounters?.disabilityCount.toString());
			emailText = emailText.replace(reBookingWeight, booking.passengerCounters?.totalWeight.toString());
			emailText = emailText.replace(reBookingRegion, booking.region?.name);
			emailText = emailText.replace(reBookingAvailabilityDate, localBookingAvailabilityDate);
			emailText = emailText.replace(reBookingTotalAmount, booking.passengerCounters?.totalAmount.toString());
			emailText = emailText.replace(reBookingOpenAmount, booking.passengerCounters?.openAmount.toString());
			emailText = emailText.replace(reBookingUpURL, booking.upUrl);
			// emailText = emailText.replace(reBookingType, this.translateCustom.translate('BOOKINGS.' + booking.bookingType, language));
		}

		return emailText;
	}
}
